<template>
  <div class="workshops-page">
    <!-- Hero Section -->
    <section class="page-hero">
      <div class="hero-overlay"></div>
      <div class="container">
        <div class="hero-content" data-aos="fade-up">
          <h1 class="page-title">Creative Workshops</h1>
          <p class="page-subtitle">Learn the art of handcrafting with our expert-led workshops. Create something beautiful with your own hands!</p>
        </div>
      </div>
    </section>

    <!-- Filter & Search Section -->
    <!--<section class="filter-section">
      <div class="container">
        <div class="filter-wrapper" data-aos="fade-up">
          <div class="filter-group">
            <label class="filter-label">Category:</label>
            <select v-model="selectedCategory" class="filter-select">
              <option value="all">All Workshops</option>
              <option value="jewelry">Jewelry Making</option>
              <option value="crafts">Crafts & Decor</option>
              <option value="painting">Painting & Art</option>
              <option value="pottery">Pottery & Clay</option>
            </select>
          </div>
          
          <div class="filter-group">
            <label class="filter-label">Price Range:</label>
            <select v-model="selectedPrice" class="filter-select">
              <option value="all">All Prices</option>
              <option value="0-500">Under ₹500</option>
              <option value="500-1000">₹500 - ₹1000</option>
              <option value="1000-2000">₹1000 - ₹2000</option>
              <option value="2000+">₹2000+</option>
            </select>
          </div>
          
          <div class="filter-group">
            <label class="filter-label">Date:</label>
            <select v-model="selectedDate" class="filter-select">
              <option value="all">All Dates</option>
              <option value="upcoming">Upcoming</option>
              <option value="this-week">This Week</option>
              <option value="this-month">This Month</option>
            </select>
          </div>
        </div>
      </div>
    </section>-->

    <!-- Workshops Grid -->
    <section class="workshops-grid">
      <div class="container">
        <!-- No workshops message -->
        <div v-if="filteredWorkshops.length === 0" class="no-workshops" data-aos="fade-up">
          <i class="fas fa-calendar-times"></i>
          <h3>No workshops found</h3>
          <p>Try adjusting your filters or check back soon for new workshops!</p>
        </div>
        
        <!-- Workshops list -->
        <div v-else class="row g-4">
          <div
            class="col-lg-6"
            v-for="(workshop, index) in filteredWorkshops"
            :key="workshop._id"
            :data-aos="'fade-up'"
            :data-aos-delay="index * 100"
          >
            <div class="workshop-card">
              <!-- Workshop Image (single image, dynamic) -->
              <div class="workshop-image-section">
                <div class="main-image-wrapper">
                  <img 
                    :src="workshop.imageUrl || 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=600&h=400&fit=crop'" 
                    :alt="workshop.title" 
                    class="workshop-main-image"
                  >
                  
                  <!-- Availability Badge -->
                  <div class="availability-badge" :class="getAvailabilityClass(workshop)">
                    {{ getAvailabilityText(workshop) }}
                  </div>
                  
                  <!-- Category Tag -->
                  <div class="category-tag">
                    <i class="fas fa-tag"></i>
                    {{ workshop.category || 'General' }}
                  </div>
                </div>
              </div>
              
              <!-- Workshop Details -->
              <div class="workshop-details">
                <h3 class="workshop-title">{{ workshop.title }}</h3>
                <p class="workshop-description">{{ workshop.description || 'Join us for an exciting hands-on workshop where you\'ll learn new skills and create something beautiful!' }}</p>
                
                <div class="workshop-info text-start">
                  <div class="info-row text-start">
                    <div class="info-item">
                      <i class="fas fa-calendar-alt"></i>
                      <span>{{ formatDate(workshop.date) }}</span>
                    </div>
                    <div class="info-item">
                      <i class="fas fa-clock"></i>
                      <span>{{ workshop.time }}</span>
                    </div>
                  </div>
                  
                  <div class="info-row">
                    <div class="info-item">
                      <i class="fas fa-map-marker-alt"></i>
                      <span>{{ workshop.address }}</span>
                    </div>
                  </div>
                  
                  <div class="info-row">
                    <div class="info-item">
                      <i class="fas fa-users"></i>
                      <span>15 participants max</span>
                    </div>
                    <div class="info-item">
                      <i class="fas fa-hourglass-half"></i>
                      <span>3 hours</span>
                    </div>
                  </div>

                  <div class="info-row">
                    <div class="info-item">
                      <i class="fas fa-signal"></i>
                      <span>Beginner</span>
                    </div>
                  </div>
                </div>
                
                <!-- What You'll Learn -->
                <div class="learning-section">
                  <h4 class="learning-title">What You'll Learn:</h4>
                  <ul class="learning-list">
                    <li v-for="(point, idx) in (workshop.learningPoints || defaultLearningPoints).slice(0, 3)" :key="idx">
                      <i class="fas fa-check-circle"></i>
                      {{ point }}
                    </li>
                  </ul>
                </div>
                
                <!-- Materials Included -->
                <div class="materials-badge">
                  <i class="fas fa-box"></i>
                  <span>All materials included</span>
                </div>
                
                <!-- Footer with Price & CTA -->
                <!-- Footer with Price & CTA -->
                <div class="workshop-footer">
                  <div class="price-section">
                    <span v-if="workshop.originalPrice" class="price-original">₹{{ workshop.originalPrice }}</span>
                    <span class="price-current">₹{{ workshop.price }}</span>
                    <span v-if="workshop.originalPrice" class="discount-badge">
                      Save {{ calculateDiscount(workshop.originalPrice, workshop.price) }}%
                    </span>
                  </div>
                  <div class="cta-buttons">
                    <button
                      @click="registerOnWhatsapp(workshop)"
                      class="btn-whatsapp-register"
                      :disabled="!isAvailable(workshop)"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style="vertical-align:-2px;"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.549 4.107 1.51 5.833L.057 23.535a.75.75 0 0 0 .916.927l5.834-1.53A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.708 9.708 0 0 1-4.95-1.355l-.355-.211-3.666.962.978-3.58-.231-.368A9.715 9.715 0 0 1 2.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/></svg>
                      Register via WhatsApp
                    </button>
                    <button
                      @click="openRegistrationModal(workshop)"
                      class="btn-register"
                      :disabled="!isAvailable(workshop)"
                    >
                      <i class="fas fa-user-plus" />
                      {{ getButtonText(workshop) }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Join Section -->
    <section class="why-join-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title-modern">Why Join Our Workshops?</h2>
          <div class="title-underline"></div>
        </div>
        
        <div class="row g-4">
          <div class="col-md-4" data-aos="fade-up" data-aos-delay="0">
            <div class="benefit-card">
              <div class="benefit-icon">
                <i class="fas fa-chalkboard-teacher"></i>
              </div>
              <h4>Expert Instructors</h4>
              <p>Learn from experienced artisans and crafters</p>
            </div>
          </div>
          
          <div class="col-md-4" data-aos="fade-up" data-aos-delay="100">
            <div class="benefit-card">
              <div class="benefit-icon">
                <i class="fas fa-tools"></i>
              </div>
              <h4>All Materials Included</h4>
              <p>Everything you need is provided</p>
            </div>
          </div>
            
          <div class="col-md-4" data-aos="fade-up" data-aos-delay="300">
            <div class="benefit-card">
              <div class="benefit-icon">
                <i class="fas fa-coffee"></i>
              </div>
              <h4>Refreshments</h4>
              <p>Enjoy complimentary snacks & drinks</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Registration Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="closeModal">
          <i class="fas fa-times"></i>
        </button>
        
        <div class="modal-header">
          <h3>Register for <b>{{ selectedWorkshop?.title }}</b></h3>
        </div>
        
        <form @submit.prevent="submitRegistration" class="registration-form text-start">
          <div class="form-group">
            <label class="form-label">Full Name *</label>
            <input 
              type="text" 
              v-model="formData.name" 
              class="form-control" 
              placeholder="Your name"
              required
            >
          </div>
          
          <div class="form-group">
            <label class="form-label">Phone Number *</label>
            <input 
              type="tel" 
              v-model="formData.phone" 
              class="form-control" 
              placeholder="+91 98765 43210"
              required
            >
          </div>
          
          <div class="form-group">
            <label class="form-label">Number of Participants *</label>
            <select v-model="formData.participants" class="form-control" required>
              <option value="1">1 Person</option>
              <option value="2">2 People</option>
              <option value="3">3 People</option>
              <option value="4">4 People</option>
            </select>
          </div>
          
          <div class="form-group">
            <label class="form-label">Special Requirements (Optional)</label>
            <textarea 
              v-model="formData.requirements" 
              class="form-control" 
              rows="3"
              placeholder="Any dietary restrictions, accessibility needs, etc."
            ></textarea>
          </div>
          
          <div class="modal-footer">
            <div class="total-price">
              <span>Total:</span>
              <strong>₹{{ calculateTotal() }}</strong>
            </div>
            <button type="submit" class="btn-submit" :disabled="isSubmitting">
              <span v-if="!isSubmitting">
                <i class="fas fa-check-circle"></i>
                Confirm Registration
              </span>
              <span v-else>
                <i class="fas fa-spinner fa-spin"></i>
                Processing...
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toast-notification';
import ProductService from "../services/ProductService";
// import WorkshopService from '../services/WorkshopService'; // Uncomment when service is ready

export default {
  name: 'WorkshopsPage',
  data() {
    return {
      workshops: [],
      selectedCategory: 'all',
      selectedPrice: 'all',
      selectedDate: 'all',
      showModal: false,
      selectedWorkshop: null,
      isSubmitting: false,
      formData: {
        name: '',
        email: '',
        phone: '',
        participants: '1',
        requirements: ''
      },
      // Static default learning points shown when backend doesn't provide them
      defaultLearningPoints: [
        'Basic polymer clay techniques',
        'Color mixing and blending',
        'Creating earrings and pendants'
      ],
      WHATSAPP_NUMBER: "919512981118", // 🔁 Replace with your number
    }
  },
  computed: {
    filteredWorkshops() {
      let filtered = this.workshops;
      
      // Filter by category
      if (this.selectedCategory !== 'all') {
        filtered = filtered.filter(w => w.category === this.selectedCategory);
      }
      
      // Filter by price
      if (this.selectedPrice !== 'all') {
        const [min, max] = this.selectedPrice.split('-').map(p => p.replace('+', ''));
        filtered = filtered.filter(w => {
          if (max) {
            return w.price >= parseInt(min) && w.price <= parseInt(max);
          } else {
            return w.price >= parseInt(min);
          }
        });
      }
      
      // Filter by date (implement based on your date logic)
      if (this.selectedDate !== 'all') {
        // Add your date filtering logic here
      }
      
      return filtered;
    }
  },
  methods: {
    getWorkshops() {
      ProductService.getHomePageData()
        .then((response) => {
          this.workshops = response.data.data.workshops;
        })
        .catch((e) => {
          const $toast = useToast();
          $toast.error(e.response?.data?.respones_msg || 'Failed to load workshops');
        });
    },
    registerOnWhatsapp(workshop) {
      if (!this.isAvailable(workshop)) return;
      const message =
        `Hi! I'd like to register for a workshop\n\n` +
        `*Workshop:* ${workshop.title}\n` +
        `*Date:* ${this.formatDate(workshop.date)}\n` +
        `*Time:* ${workshop.time}\n` +
        `*Venue:* ${workshop.address}\n` +
        `*Price:* Rs.${workshop.price} per person\n\n` +
        `Please confirm my spot and share payment details. Thank you!`;
      window.open(
        `https://wa.me/${this.WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
        '_blank'
      );
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    },
    
    getAvailabilityClass(workshop) {
      const registered = workshop.registeredCount || 0;
      const max = workshop.maxParticipants || 15;
      
      if (registered >= max) return 'full';
      if (registered >= max * 0.8) return 'filling-fast';
      return 'available';
    },
    
    getAvailabilityText(workshop) {
      const registered = workshop.registeredCount || 0;
      const max = workshop.maxParticipants || 15;
      
      if (registered >= max) return 'Fully Booked';
      if (registered >= max * 0.8) return 'Filling Fast!';
      return `${max - registered} spots left`;
    },
    
    isAvailable(workshop) {
      const registered = workshop.registeredCount || 0;
      const max = workshop.maxParticipants || 15;
      return registered < max;
    },
    
    getButtonText(workshop) {
      if (!this.isAvailable(workshop)) return 'Fully Booked';
      return 'Register Now';
    },
    
    calculateDiscount(original, current) {
      return Math.round(((original - current) / original) * 100);
    },
    
    openRegistrationModal(workshop) {
      if (!this.isAvailable(workshop)) return;
      this.selectedWorkshop = workshop;
      this.showModal = true;
      document.body.style.overflow = 'hidden';
    },
    
    closeModal() {
      this.showModal = false;
      this.selectedWorkshop = null;
      this.formData = {
        name: '',
        email: '',
        phone: '',
        participants: '1',
        requirements: ''
      };
      document.body.style.overflow = 'auto';
    },
    
    calculateTotal() {
      if (!this.selectedWorkshop) return 0;
      return this.selectedWorkshop.price * parseInt(this.formData.participants);
    },
    
    async submitRegistration() {
      const $toast = useToast();
      this.isSubmitting = true;
      
      try {
        const orderResponse = await ProductService.submitWorkshopRegistration({
          workshopId: this.selectedWorkshop._id,
          name: this.formData.name,
          phoneNumber: this.formData.phone,
          totalPerson: parseInt(this.formData.participants),
          notes: this.formData.requirements  // ← was this.customerNotes (undefined!)
        });
        
        const { razorpayOrder, customer } = orderResponse.data.data;
        
        const options = {
          key: process.env.VUE_APP_RAZORPAY_KEY,
          amount: razorpayOrder.amount,
          currency: razorpayOrder.currency,
          name: "Bee's Atelier",
          description: `Workshop: ${this.selectedWorkshop.title}`,
          order_id: razorpayOrder.id,
          prefill: {
            name: customer.name,
            contact: customer.phone
          },
          theme: {
            color: "#76ACAF"
          },
          handler: async (response) => {
            // ✅ verifyPayment is called here, AFTER payment succeeds
            await this.verifyPayment(response, this.selectedWorkshop._id);
            this.closeModal(); // ✅ Close only after successful payment
          },
          modal: {
            ondismiss: () => {
              this.isSubmitting = false;
              this.closeModal(); // ✅ Close only after successful payment
              $toast.info('Payment cancelled');
              // Don't close modal — let user try again
            }
          }
        };
        
        const rzp = new window.Razorpay(options);
        
        rzp.on("payment.failed", (response) => {
          this.closeModal(); // ✅ Close only after successful payment
          $toast.error(response.error.description || 'Payment failed');
          this.isSubmitting = false;
        });
        
        rzp.open();
        
      } catch (error) {
        this.closeModal(); // ✅ Close only after successful payment
        console.log({ error });
        $toast.error('Registration failed. Please try again.');
      } finally {
        this.isSubmitting = false;
      }
    },

    async verifyPayment(razorpayResponse, orderId) {
      const $toast = useToast();
      
      try {
        const verifyResponse = await ProductService.verifyWorkshopPayment({
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature,
          orderId: orderId
        });
        
        $toast.success('Payment successful! Your order has been placed.');
        
        // Redirect to order confirmation page
        this.$router.push({
          name: 'Workshop Payment Success',
          query: { workshopId: orderId, registrationId: verifyResponse.data.data } // Using query instead of params
        });
        
      } catch (error) {
        console.error('Payment Verification Error:', error);
        $toast.error('Payment verification failed. Please contact support.');
        this.isProcessing = false;
      }
    },
  },
  mounted() {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-in-out',
        once: true,
        offset: 100
      });
    }
    
    this.getWorkshops();
  },
  beforeUnmount() {
    document.body.style.overflow = 'auto';
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Playfair+Display:wght@400;500;600;700&display=swap');

.workshops-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ========== HERO SECTION ========== */
.page-hero {
  height: 300px;
  background: linear-gradient(135deg, rgba(118, 172, 175, 0.9), rgba(98, 152, 155, 0.9));
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-top: 80px;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
}

.hero-content {
  text-align: center;
  color: white;
  position: relative;
  z-index: 2;
}

.page-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.page-subtitle {
  font-size: clamp(1rem, 2vw, 1.2rem);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
}

/* ========== FILTER SECTION ========== */
.filter-section {
  padding: 40px 0;
  background: white;
}

.filter-wrapper {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-weight: 600;
  color: #2d3748;
  font-size: 0.9rem;
}

.filter-select {
  padding: 10px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  min-width: 200px;
  transition: all 0.3s;
  font-family: 'Inter', sans-serif;
}

.filter-select:focus {
  outline: none;
  border-color: rgb(118, 172, 175);
  box-shadow: 0 0 0 3px rgba(118, 172, 175, 0.1);
}

/* ========== WORKSHOPS GRID ========== */
.workshops-grid {
  padding: 60px 0 100px 0;
  background: #f8fafb;
}

.no-workshops {
  text-align: center;
  padding: 80px 20px;
  color: #718096;
}

.no-workshops i {
  font-size: 4rem;
  color: rgb(118, 172, 175);
  margin-bottom: 20px;
}

.no-workshops h3 {
  font-size: 1.5rem;
  color: #2d3748;
  margin-bottom: 10px;
}

/* Workshop Card */
.workshop-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: all 0.4s;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.workshop-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 40px rgba(0,0,0,0.15);
}

/* Image Section */
.workshop-image-section {
  position: relative;
}

.main-image-wrapper {
  position: relative;
  overflow: hidden;
  height: 260px;
}

.workshop-main-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s;
}

.workshop-card:hover .workshop-main-image {
  transform: scale(1.1);
}

.availability-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
  backdrop-filter: blur(10px);
}

.availability-badge.available {
  background: rgba(72, 187, 120, 0.9);
  color: white;
}

.availability-badge.filling-fast {
  background: rgba(237, 137, 54, 0.9);
  color: white;
  animation: pulse 2s infinite;
}

.availability-badge.full {
  background: rgba(245, 101, 101, 0.9);
  color: white;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.category-tag {
  position: absolute;
  top: 15px;
  left: 15px;
  background: rgba(255, 255, 255, 0.95);
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgb(118, 172, 175);
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: capitalize;
}

/* Thumbnail Gallery */
.thumbnail-gallery {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 5px;
  padding: 5px;
  background: #f8f9fa;
}

.thumbnail-item {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer;
}

.thumbnail-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.thumbnail-item:hover img {
  transform: scale(1.1);
}

.more-images {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.7);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
}

/* Workshop Details */
.workshop-details {
  padding: 25px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.workshop-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 12px;
}

.workshop-description {
  color: #718096;
  line-height: 1.7;
  margin-bottom: 20px;
  font-size: 0.95rem;
}

.workshop-info {
  margin-bottom: 20px;
}

.info-row {
  display: flex;
  gap: 20px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4a5568;
  font-size: 0.9rem;
  flex: 1;
  min-width: 180px;
}

.info-item i {
  color: rgb(118, 172, 175);
  width: 16px;
}

/* Learning Section */
.learning-section {
  margin-bottom: 20px;
  padding: 15px;
  background: #f8fafb;
  border-radius: 12px;
}

.learning-title {
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
  font-size: 0.95rem;
}

.learning-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.learning-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #4a5568;
  font-size: 0.9rem;
  margin-bottom: 8px;
}

.learning-list i {
  color: rgb(118, 172, 175);
  margin-top: 3px;
  flex-shrink: 0;
}

.materials-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(118, 172, 175, 0.1);
  color: rgb(118, 172, 175);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 20px;
}

/* Workshop Footer */
.workshop-footer {
  margin-top: auto;
  padding-top: 20px;
  border-top: 2px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.price-section {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.price-original {
  color: #a0aec0;
  text-decoration: line-through;
  font-size: 1rem;
}

.price-current {
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d3748;
}

.discount-badge {
  background: #f93a5a;
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
}

.btn-register {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 28px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s;
  text-decoration: none;
}

.btn-register:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(118, 172, 175, 0.4);
  color: white;
}

.btn-register:disabled {
  background: #cbd5e0;
  cursor: not-allowed;
  transform: none;
}

/* ========== WHY JOIN SECTION ========== */
.why-join-section {
  padding: 100px 0;
  background: white;
}

.section-header {
  text-align: center;
  margin-bottom: 10px;
}

.section-title-modern {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2rem, 4vw, 2rem);
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 5px;
}

.title-underline {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, rgb(118, 172, 175), rgb(98, 152, 155));
  margin: 0 auto;
  border-radius: 2px;
  margin-bottom: 40px;
}

.benefit-card {
  text-align: center;
  padding: 30px 20px;
  transition: transform 0.3s;
}

.benefit-card:hover {
  transform: translateY(-5px);
}

.benefit-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(118, 172, 175, 0.1), rgba(98, 152, 155, 0.1));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.benefit-icon i {
  font-size: 2rem;
  color: rgb(118, 172, 175);
}

.benefit-card h4 {
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
}

.benefit-card p {
  color: #718096;
  font-size: 0.95rem;
  line-height: 1.6;
}

/* ========== MODAL ========== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  backdrop-filter: blur(5px);
}

.modal-content {
  background: white;
  border-radius: 20px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0,0,0,0.1);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 1;
}

.modal-close:hover {
  background: rgba(0,0,0,0.2);
  transform: rotate(90deg);
}

.modal-header {
  padding: 40px 40px 20px 40px;
  border-bottom: 2px solid #e2e8f0;
}

.modal-header h3 {
  font-family: 'Playfair Display', serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 5px;
}

.modal-header p {
  color: rgb(118, 172, 175);
  font-weight: 600;
}

.registration-form {
  padding: 30px 40px 40px 40px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
  font-size: 0.95rem;
}

.form-control {
  width: 100%;
  padding: 12px 18px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s;
  font-family: 'Inter', sans-serif;
}

.form-control:focus {
  outline: none;
  border-color: rgb(118, 172, 175);
  box-shadow: 0 0 0 3px rgba(118, 172, 175, 0.1);
}

textarea.form-control {
  resize: vertical;
  min-height: 100px;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 2px solid #e2e8f0;
  margin-top: 20px;
}

.total-price {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.total-price span {
  color: #718096;
  font-size: 0.9rem;
}

.total-price strong {
  font-size: 1.8rem;
  color: #2d3748;
}

.btn-submit {
  padding: 14px 32px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(118, 172, 175, 0.4);
}

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

/* ========== MOBILE RESPONSIVE ========== */
@media (max-width: 768px) {
  .page-hero {
    height: 300px;
    margin-top: 70px;
  }
  
  .filter-wrapper {
    flex-direction: column;
  }
  
  .filter-select {
    min-width: 100%;
  }
  
  .workshops-grid {
    padding: 40px 0 60px 0;
  }
  
  .workshop-details {
    padding: 20px;
  }
  
  .workshop-footer {
    flex-direction: column;
    align-items: stretch;
  }
  
  .btn-register {
    width: 100%;
    justify-content: center;
  }
  
  .why-join-section {
    padding: 60px 0;
  }
  
  .modal-content {
    margin: 0;
    border-radius: 20px 20px 0 0;
    max-height: 95vh;
  }
  
  .modal-header,
  .registration-form {
    padding: 30px 20px;
  }
  
  .modal-footer {
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
  }
  
  .btn-submit {
    width: 100%;
    justify-content: center;
  }

  .info-item {
    min-width: 140px;
  }
}

.cta-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn-whatsapp-register {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #25D366;
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-whatsapp-register:hover:not(:disabled) {
  background: #1ebe57;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(37, 211, 102, 0.35);
}

.btn-whatsapp-register:disabled {
  background: #cbd5e0;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 768px) {
  .cta-buttons {
    width: 100%;
    flex-direction: column;
  }
  .btn-whatsapp-register {
    width: 100%;
    justify-content: center;
  }
}
</style>